import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, FileText, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function SmartFloorPage() {
  const {
    t
  } = useLanguage();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const advantages = [t('floor.adv1'), t('floor.adv2'), t('floor.adv3'), t('floor.adv4'), t('floor.adv5'), t('floor.adv6')];
  return <div className="w-full bg-white pb-32" data-id="element-632">
      {/* Hero */}
      <div className="bg-corporate-dark text-white py-24 md:py-40 px-4 relative overflow-hidden" data-id="element-633">
        <div className="absolute inset-0 opacity-20" data-id="element-634">
          <img src="https://cdn.magicpatterns.com/uploads/eGA34zCj5uVtwCRtBqDcZr/suelos_SPC).png" alt="Background" className="w-full h-full object-cover" data-id="element-635" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 mt-10" data-id="element-636">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-637">
            <span className="text-accent-beige font-medium tracking-[0.2em] uppercase text-sm mb-6 block" data-id="element-638">
              {t('prod.line')}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 tracking-tight" data-id="element-639">
              SmartFloor
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl font-light leading-relaxed" data-id="element-640">
              {t('floor.hero.desc')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28" data-id="element-641">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-642">
          {/* Content */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-643">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-644">
              {t('floor.sec.title')}
            </h2>
            <div className="text-lg text-gray-600 font-light leading-relaxed space-y-6 mb-12" data-id="element-645">
              <p data-id="element-646">{t('floor.sec.p1')}</p>
              <p data-id="element-647">{t('floor.sec.p2')}</p>
            </div>

            <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-648">
              {t('prod.adv.title')}
            </h3>
            <ul className="space-y-5 mb-12" data-id="element-649">
              {advantages.map((adv, idx) => <li key={idx} className="flex items-start" data-id="element-650">
                  <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-651" />
                  <span className="text-gray-700 text-lg font-light" data-id="element-652">
                    {adv}
                  </span>
                </li>)}
            </ul>

            <a href="#documentacion-tecnica" onClick={e => {
            e.preventDefault();
            setOpenAccordion('catalogos');
            setTimeout(() => {
              document.getElementById('documentacion-tecnica')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 50);
          }} className="inline-flex items-center px-8 py-4 bg-neutral-grey text-corporate-dark font-medium rounded-md hover:bg-gray-200 hover:scale-[1.02] transition-all duration-500 ease-premium" data-id="element-653">
              <Download className="mr-3 h-5 w-5" data-id="element-654" />
              {t('prod.dl.btn')}
            </a>
          </motion.div>

          {/* Images/Applications */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="space-y-8" data-id="element-655">
            <div className="h-96 bg-gray-100 rounded-sm overflow-hidden group" data-id="element-656">
              <img src="https://cdn.magicpatterns.com/uploads/eGA34zCj5uVtwCRtBqDcZr/suelos_SPC).png" alt="Aplicación SmartFloor" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-657" />
            </div>
            <div className="grid grid-cols-2 gap-8" data-id="element-658">
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-659">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-660">
                  {t('floor.app1.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-661">
                  {t('floor.app1.desc')}
                </p>
              </div>
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-662">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-663">
                  {t('floor.app2.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-664">
                  {t('floor.app2.desc')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Características Técnicas */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mt-24 lg:mt-32" data-id="element-665">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-666">
            {t('floor.specs.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12 max-w-3xl leading-relaxed" data-id="element-667">
            {t('floor.specs.intro')}
          </p>

          {/* Características destacadas */}
          <div className="mb-16" data-id="element-668">
            <h4 className="text-xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-669">
              {t('floor.specs.features.title')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-670">
              {[t('floor.specs.f1'), t('floor.specs.f2'), t('floor.specs.f3'), t('floor.specs.f4'), t('floor.specs.f5'), t('floor.specs.f6')].map((feature, idx) => <div key={idx} className="flex items-start" data-id="element-671">
                  <CheckCircle2 className="h-5 w-5 text-corporate-blue mr-3 flex-shrink-0 mt-0.5" data-id="element-672" />
                  <span className="text-gray-700 text-base font-light leading-relaxed" data-id="element-673">
                    {feature}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Documentación Técnica */}
        <motion.div id="documentacion-tecnica" initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mt-24 lg:mt-32 scroll-mt-24" data-id="element-674">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-675">
            {t('prod.docs.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12" data-id="element-676">
            {t('prod.docs.floor.desc')}
          </p>

          {[{
          id: 'catalogos',
          title: t('prod.docs.catalogs'),
          docs: [{
            name: 'Catálogo SPC Static Pro',
            url: 'https://drive.google.com/uc?export=download&id=1rWzjnj_XxpG9ZI41Dv4ZklwnkjUOVHIu'
          }, {
            name: 'SPC New Designs',
            url: 'https://drive.google.com/uc?export=download&id=1rx5l0D3CiLPa9YYLxGzaFy6xNQLa_Q6p'
          }]
        }, {
          id: 'fichas',
          title: t('prod.docs.techSheets'),
          docs: [{
            name: 'Ficha Técnica R10',
            url: 'https://drive.google.com/file/d/1QQIwxAHx4eecM5evNd0XxKYJ_sC9JQlc/view?usp=sharing'
          }, {
            name: 'Clasificación Antideslizante R11',
            url: 'https://drive.google.com/uc?export=download&id=1P4KgIx6CR4fs4qCXed9ucR73zExC7dvt'
          }, {
            name: 'Ficha Técnica Zócalo SPC',
            url: 'https://drive.google.com/uc?export=download&id=1RCsWIgwO08R1nnM2GLA0y_7McZ8wQHp2'
          }]
        }].map(category => <div key={category.id} className="bg-white border border-gray-100 rounded-sm shadow-md overflow-hidden mb-4" data-id="element-677">
              <button onClick={() => setOpenAccordion(openAccordion === category.id ? null : category.id)} className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-neutral-grey transition-colors duration-300" data-id="element-678">
                <div data-id="element-679">
                  <h4 className="text-xl font-heading font-bold text-corporate-dark tracking-tight" data-id="element-680">
                    {category.title}
                  </h4>
                  <p className="text-sm text-gray-500 font-light mt-1" data-id="element-681">
                    {category.docs.length}{' '}
                    {category.docs.length === 1 ? t('prod.docs.count.one') : t('prod.docs.count.many')}
                  </p>
                </div>
                <ChevronDown className={`h-5 w-5 text-corporate-blue flex-shrink-0 ml-4 transition-transform duration-300 ${openAccordion === category.id ? 'rotate-180' : ''}`} data-id="element-682" />
              </button>
              <AnimatePresence initial={false} data-id="element-683">
                {openAccordion === category.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="overflow-hidden" data-id="element-684">
                    <div className="divide-y divide-gray-100 border-t border-gray-100" data-id="element-685">
                      {category.docs.map((doc, idx) => <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between px-8 py-5 hover:bg-neutral-grey transition-colors duration-300" data-id="element-686">
                          <div className="flex items-center mb-4 sm:mb-0" data-id="element-687">
                            <FileText className="h-5 w-5 text-corporate-blue mr-4 flex-shrink-0" data-id="element-688" />
                            <span className="text-base text-gray-800 font-medium" data-id="element-689">
                              {doc.name}
                            </span>
                          </div>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center justify-center px-5 py-2.5 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-500 ease-premium rounded-md text-sm font-medium w-full sm:w-auto hover:scale-[1.02]" data-id="element-690">
                            <Download className="mr-2 h-4 w-4" data-id="element-691" />
                            {t('prod.docs.download')}
                          </a>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </motion.div>
      </div>
    </div>;
}